import React, {useEffect, useMemo, useRef, useState} from "react";
import "../Survey/SurveyPage.css";
import facebookIcon from "../icons/facebook-content.svg";
import instagramIcon from "../icons/instagram-content.svg";
import linkedInIcon from "../icons/linkedin-content.svg";
import copyIcon from "../icons/copy-icon.svg";
import penIcon from "../icons/pen-icon.svg";
import checkIcon from "../icons/check-icon-black.svg";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {contentPlanActions, postActions, questionsActions} from "../../store"; // kept exactly
import {
    Layout,
    Row,
    Col,
    Card,
    Tabs,
    Input,
    Typography,
    Divider,
    Avatar,
    Tag,
    Badge,
    Switch,
    Progress,
    Segmented,
    Button as AntButton,
    message,
    notification,
    Button,
    Space,
    Select,
    DatePicker,
    Popconfirm,
    Popover// keep your existing Button import too
} from "antd";
import {
    SendOutlined, ThunderboltOutlined, EyeOutlined, CalendarOutlined, ClockCircleOutlined,
    InstagramOutlined, LinkedinOutlined, FacebookOutlined, PictureOutlined, VideoCameraOutlined,
    ScissorOutlined, CopyOutlined, EditOutlined, CheckOutlined, InfoCircleFilled, QuestionCircleOutlined
} from "@ant-design/icons";
import PageHeader from "../../components/Header/PageHeader"; // keep header component
import moment from "moment";
import {fetchWrapper} from "../../helpers";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {daysOfWeek} from "../../constants/daysOfWeek";
import AppNavigation from "../../components/AppNavigation";
import PostPhotoEditor from "../../components/Editors/PostPhotoEditor";
import PostPhotoCuter from "../../components/Editors/PostPhotoCuter";
import PostRegenerateEditor from "../../components/Editors/PostContentEditor/PostRegenerateEditor";
import LivePreviewCard from "../../components/Cards/LivePreviewCard"
import {StickyNavigationHeader} from "../../components/Navigation/StickyNavigationHeader";

// ---------------------------------
// Constants & helpers
// ---------------------------------
const baseUrl = `${process.env.REACT_APP_API_URL}`;
const PER_IMAGE_LIMIT = 8 * 1024 * 1024; // 8 MB per image
const IMAGES_TOTAL_LIMIT = 80 * 1024 * 1024; // 80 MB total (images only)
const COMBINED_WARN_LIMIT = 64 * 1000 * 1000; // 64 MB warning (kept for parity)
const MAX_GENERATE_IMAGE_TRIES = 3; // unchanged

const networkIcons = {
    Facebook: facebookIcon,
    Instagram: instagramIcon,
    LinkedIn: linkedInIcon,
};

const useNotify = () => {
    const [api, context] = notification.useNotification();
    return {api, context};
};

const useSnTokens = () => {
    const [tokens, setTokens] = useState({facebook: false, instagram: false, linkedin: false});
    useEffect(() => {
        fetchWrapper
            .get(`${baseUrl}/auth/getSocialMediaTokens/`)
            .then((res) => {
                const next = {facebook: false, instagram: false, linkedin: false};
                res.forEach((t) => {
                    if (t.socialMediaType === "FACEBOOK") next.facebook = true;
                    if (t.socialMediaType === "INSTAGRAM") next.instagram = true;
                    if (t.socialMediaType === "LINKEDIN") next.linkedin = true;
                });
                setTokens(next);
            })
            .catch(() => {
            });
    }, []);
    return tokens;
};

// ---------------------------------
// Component
// ---------------------------------
export const PostPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {contentId} = useParams(); // kept though unused

    // Store slices (unchanged)
    const user = useSelector((s) => s.auth.user);
    const post = useSelector((s) => s.post.post);
    const {loading: postLoading, generating: postGenerating} = useSelector((s) => s.post);
    const contentPlan = useSelector((s) => s.contentPlan.contentPlan);

    // Local state
    const videoPlayerRef = useRef(null);
    const hiddenVideoInput = useRef(null);
    const hiddenFileInput = useRef(null);

    const [images, setImages] = useState([]);
    const [video, setVideo] = useState(null);
    const [generating, setGenerating] = useState(false);

    const [editMainText, setEditMainText] = useState(false);
    const [editHashtag, setEditHashtag] = useState(false);
    const [editOffer, setEditOffer] = useState(false);

    const [mainText, setMainText] = useState(post?.post ?? "");
    const [hashtags, setHashtags] = useState(post?.hashtags ?? "");
    const [offer, setOffer] = useState(post?.media ?? "");

    const [publishTime, setPublishTime] = useState(contentPlan.date);
    const [weekDay, setWeekDay] = useState(contentPlan.weekDay || "");

    const [dragActive, setDragActive] = useState(false);
    const snTokens = useSnTokens();
    const [snForAnotherPost, setSnForAnotherPost] = useState([]);

    const isReadOnly = Boolean(post?.published);

    const {api: notify, context: notificationContextHolder} = useNotify();

    // ---------------------------------
    // Effects
    // ---------------------------------
    useEffect(() => {
        const initial = [];
        if (contentPlan.socialNetwork?.includes("LinkedIn")) initial.push("LinkedIn");
        if (contentPlan.socialNetwork?.includes("Facebook")) initial.push("Facebook");
        if (contentPlan.socialNetwork?.includes("Instagram")) initial.push("Instagram");
        setSnForAnotherPost(initial);
    }, []);

    useEffect(() => {
        // Facebook page selection hint
        if (contentPlan.socialNetwork?.toLowerCase().includes("facebook")) {
            fetchWrapper.get(`${baseUrl}/profile/`).then((res) => {
                if (!res.facebookPageId) openNotificationFbPages();
            });
        }
        // Instagram requires media
        if (contentPlan.socialNetwork?.toLowerCase().includes("instagram") && (!post?.images?.length && !post?.video)) {
            message.info(t("functions.minMediaForInstagramPostMessage"));
            window.scrollTo({top: 9999, behavior: "smooth"});
        }
    }, []);

    useEffect(() => {
        // Keep legacy combined 10MB warning
        const total = [...images, video].reduce((acc, m) => acc + (m?.size || 0), 0);
        if (total > COMBINED_WARN_LIMIT) message.error(t("post.bigMediaListSize", {size: 64}));
    }, [images, video]);

    // ---------------------------------
    // Notifications
    // ---------------------------------
    const openNotificationSocialNetwork = ({message: msg, description}) => {
        const key = `open${Date.now()}`;
        const btn = (
            <Space>
                <Button type="primary" size="small"
                        onClick={() => navigate("/profile")}>{t("posts.goToProfile")}</Button>
            </Space>
        );
        notify.open({
            message: msg || t("functions.error"),
            description: description || t("posts.unknownError"),
            btn,
            key
        });
    };

    const openNotificationFbPages = () => {
        const key = `open${Date.now()}`;
        const btn = (
            <Space>
                <Button type="primary" size="small"
                        onClick={() => navigate("/profile?helpFbModal=true")}>{t("posts.goToProfile")}</Button>
            </Space>
        );
        notify.open({message: t("functions.warning"), description: t("posts.notSelectedFacebookPage"), btn, key});
    };

    // ---------------------------------
    // Helpers
    // ---------------------------------
    const clipboardCopy = (text) => {
        navigator.clipboard.writeText(text);
        message.success(t("functions.copied"));
    };

    const enforceTextLimits = () => {
        const textToCheck = editMainText ? mainText : post?.post;
        if (!textToCheck) return true;

        const len = textToCheck.length;
        let ok = true;
        let badNet = null;

        contentPlan.socialNetwork.split(",").forEach((n) => {
            const k = n.trim().toLowerCase();
            if (k.includes("instagram") && len > 2200) {
                badNet = "Instagram";
                ok = false;
            }
            if (k.includes("linkedin") && len > 3000) {
                badNet = "LinkedIn";
                ok = false;
            }
            if (k.includes("facebook") && len > 8000) {
                badNet = "Facebook";
                ok = false;
            }
        });

        if (!ok) {
            const key = `char-limit-${Date.now()}`;
            const limit = badNet === "Instagram" ? 2200 : badNet === "LinkedIn" ? 3000 : 8000;
            notify.warning({
                message: t("functions.warning"),
                description: (
                    <>
                        {t("functions.characterLimitError", {network: badNet, limit})}
                        <div style={{marginTop: 10}}>{t("functions.pleaseEditPost")}</div>
                    </>
                ),
                btn: (
                    <Button type="primary" size="small" onClick={() => notify.destroy(key)}>
                        {t("functions.close")}
                    </Button>
                ),
                key,
                duration: 0,
            });
        }
        return ok;
    };

    const patchPost = async (partial) => {
        try {
            const res = await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, partial);
            return res;
        } catch (e) {
            message.error(t("functions.wrong"));
            throw e;
        }
    };

    const requireInstagramMedia = () => {
        if (contentPlan.socialNetwork.toLowerCase().includes("instagram") && (!post?.images?.length && !post?.video && !images.length && !video)) {
            message.warning(t("functions.minMediaForInstagramPostMessage"));
            return false;
        }
        return true;
    };

    // ---------------------------------
    // Change handlers (server updates preserved)
    // ---------------------------------
    const changeMainText = async () => {
        if (isReadOnly) {
            return message.info(t("postPage.message.cannotEditPublished") || "Published post can't be edited");
        }

        if (mainText.length > 8000) return message.error(t("functions.maxTextLength", {limit: 8000}));
        if (!mainText.trim()) return message.error(t("functions.noText"));
        if (mainText.trim().length < 25) return message.error(t("functions.minTextLength", {limit: 25}));

        if (editMainText) {
            const res = await patchPost({post: mainText});
            if (res) {
                dispatch(postActions.updatePost({
                    postId: post.id,
                    contentPlanId: contentPlan.id,
                    postData: {post: mainText},
                }))
                message.success(t("functions.changesSaved"))
            }
        }
        setEditMainText((v) => !v);
    };

    const changeHashtags = async () => {
        if (isReadOnly) {
            return message.info(t("postPage.message.cannotEditPublished") || "Published post can't be edited");
        }

        if (editHashtag) {
            if (!hashtags.trim()) return message.error(t("functions.noText"));
            const res = await patchPost({hashtags});
            if (res) {
                dispatch(postActions.updatePost({
                    postId: post.id,
                    contentPlanId: contentPlan.id,
                    postData: {hashtags},
                }));
                message.success(t("functions.changesSaved"))
            }
        }
        setEditHashtag((v) => !v);
    };

    const changeOffer = async () => {
        if (isReadOnly) {
            return message.info(t("post.cannotEditPublished") || "Published post can't be edited");
        }

        if (editOffer) {
            const res = await patchPost({media: offer});
            if (res) {
                dispatch(postActions.updatePost({
                    postId: post.id,
                    contentPlanId: contentPlan.id,
                    postData: {media: offer}
                }));
                message.success(t("functions.changesSaved"));
            }
        }
        setEditOffer((v) => !v);
    };

    const changePublishTime = (iso) => {
        setPublishTime(iso);
        fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {date: iso});
    };

    const changeWeekDay = (value) => {
        fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {weekday: value}).then((res) => {
            if (res) setWeekDay(value);
        });
    };

    // ---------------------------------
    // Media: images
    // ---------------------------------
    const pushImagesWithinTotal = (prevImages, newImages) => {
        const merged = [...prevImages, ...newImages];
        const total = merged.reduce((s, f) => s + (f?.size || 0), 0);
        if (total <= IMAGES_TOTAL_LIMIT) return merged.slice(0, 6);

        // keep as many as fit
        let acc = prevImages.reduce((s, f) => s + (f?.size || 0), 0);
        const accepted = [...prevImages];
        for (const f of newImages) {
            const next = acc + (f?.size || 0);
            if (next <= IMAGES_TOTAL_LIMIT) {
                accepted.push(f);
                acc = next;
            } else {
                message.error(t("functions.totalImagesTooLarge") || "Total images size must be under 80MB");
            }
        }
        return accepted.slice(0, 6);
    };

    const handleImageFilePicker = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setImages((prev) => pushImagesWithinTotal(prev, files));
    };

    const handleDeleteImage = (idx) => {
        if (!window.confirm(t("post.removeSingleMediaConfirm"))) return;
        setImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleResetImages = () => {
        if (!window.confirm(t("post.removeAllMediaConfirm"))) return;
        setImages([]);
    };

    const handleUpdateImages = async (updated) => {
        await dispatch(postActions.updateImages({images: updated, postId: post.id, contentPlanId: contentPlan.id}));
    };

    const handleRemoveGeneratedImage = async (index = null) => {
        if (!window.confirm(t("post.removeAllMediaConfirm"))) return;
        if (index === null) {
            await dispatch(postActions.removeImage({removeIndex: 0, postId: post.id, contentPlanId: contentPlan.id}));
        } else {
            await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {images: null, hashtags, media: offer});
            await dispatch(postActions.removeImage({removeIndex: 0, postId: post.id, contentPlanId: contentPlan.id}));
        }
    };

    // ---------------------------------
    // Media: video (logic preserved – disabled upload path)
    // ---------------------------------
    const handleAddVideo = (e) => {
        // return message.info(t("u are gae")); // keep early return (disabled)
        // unreachable legacy:
        const f = e.target.files?.[0];
        if (!f) return;
        if ((f.size / 1000000).toFixed() > 200) return message.error(t("post.maxVideoSize"));
        setVideo(f);
    };

    const handleManageMetaData = () => {
        if (videoPlayerRef.current) {
            if (videoPlayerRef.current.duration > 595) {
                message.error(t("post.maxVideoDuration"));
                setVideo(null);
            }
        }
    };

    const handleRemoveVideo = async () => {
        setVideo(null);
        if (post.video) {
            try {
                await dispatch(postActions.removeVideo({postId: post.id, contentPlanId: contentPlan.id, video}));
                window.location.reload();
            } catch (err) {
                message.error(t("functions.wrong"));
            }
        }
    };

    // Drag & drop
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const list = Array.from(e.dataTransfer.files || []);
        if (!list.length) return;

        const newImages = [];
        list.forEach((file) => {
            if (file.type.includes("image")) {
                if (file.size <= PER_IMAGE_LIMIT) newImages.push(file);
                else message.error(t("functions.imageTooLarge") || "Each image must be under 8MB");
            }
            if (file.type.includes("video")) {
                const mb = (file.size / 1_000_000);
                if (mb > 200) {
                    message.error(t("post.maxVideoSize"));
                } else {
                    setVideo(file);
                }
            }
        });

        setImages((prev) => pushImagesWithinTotal(prev, newImages));
    };

    // ---------------------------------
    // Save / Upload flow (logic preserved)
    // ---------------------------------
    const persistTextIfEditing = async () => {
        if (isReadOnly) return true;
        if (!editMainText) return true;

        const res = await patchPost({post: mainText});
        if (res) {
            dispatch(postActions.updatePost({
                postId: post.id,
                contentPlanId: contentPlan.id,
                postData: {post: mainText}
            }));
            setEditMainText(false);
            return true;
        }

        return false;
    };

    const onGenerateImage = () => {
        if (post.countGenerateImage >= MAX_GENERATE_IMAGE_TRIES) return message.error("Превышен лимит генераций изображений на пост 3 попытки.");
        if (!window.confirm(`\n${t("post.postGenerateImageLimit")}\n${t("post.postGenerateImageLeft")} ${MAX_GENERATE_IMAGE_TRIES - +post.countGenerateImage}\n${t("functions.continue")}?\n    `)) return;

        setGenerating(true);
        message.loading(t("post.generatingImage"));
        dispatch(postActions.generateImage({postId: post.id, contentPlanId: contentPlan.id}))
            .then(() => message.success(t("functions.successfullyGenerated")))
            .catch(() => message.error(t("functions.wrong")))
            .finally(() => {
                setGenerating(false);
                setImages([]);
            });
    };

    const handleToogleSnForAnotherPost = (sn) => {
        setSnForAnotherPost((prev) => {
            if (prev.includes(sn)) return prev.filter((i) => i !== sn);
            if (snTokens[sn.toLowerCase()]) return [...prev, sn];
            openNotificationSocialNetwork({
                message: t("functions.error"),
                description: t(`posts.notLinked-${sn.toLowerCase()}`)
            });
            return prev;
        });
    };

    const handleGenerateAnotherPost = () => {
        const newPostDescription = `${t("post.textToGenerateAnotherPost")}: ${post.post}`;
        const date = new Date();
        fetchWrapper
            .post(`${baseUrl}/post/save`, {
                description: newPostDescription,
                socialNetwork: snForAnotherPost.toString(),
                date: date.toISOString(),
                formatId: null
            })
            .then((res) => {
                if (res) {
                    navigate(`/create-post/${res.contentPlanId}`);
                    window.location.reload();
                }
            })
            .catch(() => message.error(t("functions.wrong")));
    };

    const onOkayButton = async () => {
        if (isReadOnly) {
            return message.info(t("post.cannotEditPublished") || "Published post can't be edited");
        }

        if (!enforceTextLimits()) return;
        if (!requireInstagramMedia()) return;

        if (!(await persistTextIfEditing())) return;

        // upload video first if present and no images (unchanged)
        if (video && !post?.images?.length) {
            try {
                await dispatch(postActions.uploadVideo({postId: post.id, contentPlanId: contentPlan.id, video}));
            } catch {
                message.error(t("functions.wrong"));
            }
        }

        // upload images
        if (images.length) {
            try {
                await dispatch(postActions.uploadImage({postId: post.id, contentPlanId: contentPlan.id, images}));
                if (contentPlan.forIndependentPosts) {
                    navigate("/posts");
                    window.location.reload();
                } else {
                    navigate("/content-plan");
                    window.location.reload();
                }
            } catch {
                message.error(t("functions.wrong"));
            }
            return;
        }

        // navigate back if nothing to upload
        if (post?.UserId && post?.UserId !== user?.id) return navigate("/moderation");
        if (contentPlan?.forIndependentPosts) {
            navigate("/posts");
            window.location.reload();
        } else {
            navigate("/content-plan");
            window.location.reload();
        }
    };

    // ---------------------------------
    // Render helpers
    // ---------------------------------
    const SocialBadges = () => (
        <div
            className="flex gap-4 justify-center py-4 border-b border-solid border-b-[color:var(--Primary-200,#E9D7FE)]">
            {contentPlan.socialNetwork?.includes("LinkedIn") && (
                <div className="flex flex-col flex-1"><img loading="lazy" src={linkedInIcon}
                                                           className="self-center w-20 aspect-square"/></div>
            )}
            {contentPlan.socialNetwork?.includes("Facebook") && (
                <div className="flex flex-col flex-1 font-medium"><img loading="lazy" src={facebookIcon}
                                                                       className="self-center w-20 aspect-square"/>
                </div>
            )}
            {contentPlan.socialNetwork?.includes("Instagram") && (
                <div className="flex flex-col flex-1"><img loading="lazy" src={instagramIcon}
                                                           className="self-center w-20 aspect-square"/></div>
            )}
        </div>
    );

    const PlannedBar = () => (
        <div className="flex justify-between px-8 mt-8 items-center">
            <img src={networkIcons[contentPlan.social_network]} width={40}/>

            <div className="flex items-center gap-1 text-[#667085]">
                {post.published ? (
                    t("post.published")
                ) : (
                    <>
                        {t("post.planned")} {contentPlan.weekDay ? (
                        <>
                            <Select onChange={changeWeekDay} value={weekDay} className="w-26 mr-0.5">
                                {daysOfWeek.map((d) => (
                                    <Select.Option key={d} value={d}>{t(`weekDays.${d}`)}</Select.Option>
                                ))}
                            </Select>
                        </>
                    ) : null}
                    </>
                )}

                {!post.published && (
                    <div>
                        {contentPlan.weekDay ? (
                            <input
                                readOnly={post.published}
                                className="ml-1 cursor-pointer border-b-2 border-violet-700 rounded-md"
                                type="time"
                                value={moment(publishTime).format("HH:mm")}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value) {
                                        const newDate = new Date(publishTime).setHours(value.split(":")[0], value.split(":")[1], 0);
                                        changePublishTime(new Date(newDate).toISOString());
                                    }
                                }}
                            />
                        ) : (
                            <DatePicker
                                locale={{dateTimeFormat: localStorage.getItem("ln") || "en"}}
                                disabledDate={(current) => current && current < moment().startOf("day")}
                                defaultValue={dayjs(new Date(publishTime))}
                                format="DD.MM.YYYY HH:mm"
                                showTime
                                onChange={(value) => value && changePublishTime(value.toISOString())}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className="flex">
                <img className="cursor-pointer" onClick={() => clipboardCopy(post.post)} src={copyIcon}/>
                {!post.published && (
                    <img className="cursor-pointer" onClick={changeMainText} src={editMainText ? checkIcon : penIcon}/>
                )}
            </div>
        </div>
    );

    // ---------------------------------
    // JSX
    // ---------------------------------
    return (
        <Layout style={{minHeight: "100vh", background: "linear-gradient(180deg, #ffffff, #faf5ff)"}}>
            {/* Top Bar */}
            <Layout.Header
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    background: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(6px)",
                    borderBottom: "1px solid #f0f0f0",
                }}
            >
                <Row align="middle" justify="space-between">
                    <Space align="center" size={12}>
                        <Button type="text" onClick={() => {
                            if (window.history.length > 1) {
                                navigate(-1);
                            } else {
                                navigate("/posts");
                            }
                        }}>
                            {t("common.back")}
                        </Button>
                        <Badge color="#7c3aed"/>
                        <Typography.Title level={4} style={{margin: 0}}>{t("post.titlePage")}</Typography.Title>
                    </Space>
                    <Space>
                        {/* keep any quick actions minimal to avoid new logic */}
                        <Button icon={<ThunderboltOutlined/>} type="default">{t("post.improveBut")}</Button>
                        <Button icon={<SendOutlined/>} type="primary" onClick={onOkayButton} disabled={isReadOnly} f>
                            {t("post.saveBut")}
                        </Button>
                    </Space>
                </Row>
            </Layout.Header>

            <Layout.Content style={{maxWidth: 1200, margin: "24px auto", width: "100%", padding: "0 16px"}}>
                {notificationContextHolder}

                <Row gutter={[16, 16]}>
                    {/* LEFT COLUMN */}
                    <Col xs={24} lg={14}>
                        {/* Target & Schedule */}
                        <Card bordered className="shadow-sm" bodyStyle={{padding: 16}}>
                            <Row align="middle" justify="space-between">
                                <Space size={12} align="center">
                                    <Typography.Title level={5}
                                                      style={{margin: 0}}>{t("postPage.cards.targetScheduleTitle")}</Typography.Title>
                                </Space>

                                <Space split={<Divider type="vertical"/>} style={{color: "#8c8c8c"}}>
                                    {contentPlan?.socialNetwork?.includes("Instagram") &&
                                        <Space><InstagramOutlined/>Instagram</Space>}
                                    {contentPlan?.socialNetwork?.includes("LinkedIn") &&
                                        <Space><LinkedinOutlined/>LinkedIn</Space>}
                                    {contentPlan?.socialNetwork?.includes("Facebook") &&
                                        <Space><FacebookOutlined/>Facebook</Space>}
                                    <Space><CalendarOutlined/>{dayjs(publishTime).format("MMM D, HH:mm")}</Space>
                                </Space>
                            </Row>

                            <Divider style={{margin: "12px 0"}}/>

                            <Row gutter={[12, 12]}>
                                {/* Weekday / Time */}
                                <Col xs={24} md={12}>
                                    <Card size="small" bordered>
                                        <Space direction="vertical" style={{width: "100%"}}>
                                            <Typography.Text strong>{t("postPage.cards.when")}</Typography.Text>

                                            {contentPlan?.weekDay ? (
                                                <div className="flex items-center gap-2">
                                                    <Select
                                                        onChange={changeWeekDay}
                                                        value={weekDay}
                                                        className="w-full"
                                                        options={daysOfWeek.map((d) => ({value: d, label: d}))}
                                                    />
                                                    <input
                                                        readOnly={post?.published}
                                                        className="border rounded px-2 py-1 w-[110px]"
                                                        type="time"
                                                        value={moment(publishTime).format("HH:mm")}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value) {
                                                                const newDate = new Date(publishTime).setHours(
                                                                    value.split(":")[0],
                                                                    value.split(":")[1],
                                                                    0
                                                                );
                                                                changePublishTime(new Date(newDate).toISOString());
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <DatePicker
                                                    disabledDate={(current) => current && current < moment().startOf("day")}
                                                    defaultValue={dayjs(new Date(publishTime))}
                                                    format="DD.MM.YYYY HH:mm"
                                                    showTime
                                                    onChange={(value) => value && changePublishTime(value.toISOString())}
                                                    style={{width: "100%"}}
                                                />
                                            )}
                                        </Space>
                                    </Card>
                                </Col>

                                {/* Recommendations */}
                                <Col xs={24} md={12}>
                                    <Card size="small" bordered>
                                        <Space direction="vertical" style={{width: "100%"}}>
                                            <Typography.Text
                                                strong>{t("postPage.cards.autoOptimizationTitle")}</Typography.Text>
                                            <Row justify="space-between" align="middle">
                                                <Typography.Text>{t("postPage.cards.bestTimeRecommendations")}</Typography.Text>
                                                <Switch defaultChecked/>
                                            </Row>
                                            <Row justify="space-between" align="middle">
                                                <Typography.Text>{t("postPage.cards.autoRescheduleOnConflict")}</Typography.Text>
                                                <Switch/>
                                            </Row>
                                        </Space>
                                    </Card>
                                </Col>
                            </Row>
                        </Card>

                        {/* Post Content */}
                        <Card className="shadow-sm" style={{marginTop: 16}} bodyStyle={{padding: 16}}>
                            <Row justify="space-between" align="middle">
                                <Typography.Title level={5}
                                                  style={{margin: 0}}>{t("postPage.cards.postContentTitle")}</Typography.Title>
                                <Space>
                                    <Button icon={<CopyOutlined/>}
                                            onClick={() => clipboardCopy(post?.post || "")}>{t("postPage.buttons.copy")}</Button>
                                    <Button
                                        icon={editMainText ? <CheckOutlined/> : <EditOutlined/>}
                                        onClick={changeMainText}
                                        disabled={isReadOnly}
                                    >
                                        {editMainText ? t("postPage.buttons.save") : t("postPage.buttons.edit")}
                                    </Button>
                                </Space>
                            </Row>

                            <Divider style={{margin: "12px 0"}}/>

                            {editMainText ? (
                                <div className="relative">
                                    <Input.TextArea
                                        value={mainText}
                                        onChange={(e) => setMainText(e.target.value)}
                                        autoSize={{minRows: 8, maxRows: 16}}
                                        maxLength={8000}
                                        placeholder="Write something engaging…"
                                    />
                                    <div
                                        className={`absolute -bottom-6 right-2 bg-white rounded px-2 py-1 text-xs ${
                                            mainText.length > 8000 ? "text-red-500" : "text-gray-600"
                                        }`}
                                    >
                                        {mainText.length} / 8000
                                    </div>
                                </div>
                            ) : (
                                <Typography.Paragraph className="!mb-0" style={{whiteSpace: "pre-line"}}>
                                    {post?.post || mainText}
                                </Typography.Paragraph>
                            )}
                        </Card>

                        {/* Hashtags */}
                        <Card className="shadow-sm" style={{marginTop: 16}} bodyStyle={{padding: 16}}>
                            <Row justify="space-between" align="middle">
                                <Typography.Title level={5}
                                                  style={{margin: 0}}>{t("postPage.cards.hashtagsTitle")}</Typography.Title>
                                <Space>
                                    <Button icon={<CopyOutlined/>}
                                            onClick={() => clipboardCopy(post?.hashtags || "")}>{t("postPage.buttons.copy")}</Button>
                                    <Button
                                        icon={editHashtag ? <CheckOutlined/> : <EditOutlined/>}
                                        onClick={changeHashtags}
                                        disabled={isReadOnly}
                                    >
                                        {editHashtag ? t("postPage.buttons.save") : t("postPage.buttons.edit")}
                                    </Button>
                                </Space>
                            </Row>
                            <Divider style={{margin: "12px 0"}}/>
                            {editHashtag ? (
                                <Input.TextArea
                                    value={hashtags}
                                    onChange={(e) => setHashtags(e.target.value)}
                                    autoSize={{minRows: 3, maxRows: 8}}
                                    placeholder="#marketing #b2b #casestudy"
                                />
                            ) : (
                                <Typography.Paragraph
                                    className="!mb-0">{post?.hashtags || hashtags}</Typography.Paragraph>
                            )}
                        </Card>

                        {/* Media suggestions */}
                        <Card className="shadow-sm" style={{marginTop: 16}} bodyStyle={{padding: 16}}>
                            <Row justify="space-between" align="middle">
                                <Typography.Title level={5}
                                                  style={{margin: 0}}>{t("postPage.cards.mediaSuggestionsTitle")}</Typography.Title>
                                <Space>
                                    <Button icon={<CopyOutlined/>}
                                            onClick={() => clipboardCopy(post?.media || "")}>{t("postPage.buttons.copy")}</Button>
                                    <Button
                                        icon={editOffer ? <CheckOutlined/> : <EditOutlined/>}
                                        onClick={changeOffer}
                                        disabled={isReadOnly}
                                    >
                                        {editOffer ? t("postPage.buttons.save") : t("postPage.buttons.edit")}
                                    </Button>
                                </Space>
                            </Row>
                            <Divider style={{margin: "12px 0"}}/>
                            {editOffer ? (
                                <Input.TextArea
                                    value={offer}
                                    onChange={(e) => setOffer(e.target.value)}
                                    autoSize={{minRows: 3, maxRows: 8}}
                                    placeholder="Suggest visuals, themes, or B-roll…"
                                />
                            ) : (
                                <Typography.Paragraph className="!mb-0">{post?.media || offer}</Typography.Paragraph>
                            )}

                            {/* Help */}
                            {!post?.published && (
                                <Popover
                                    overlayStyle={{maxWidth: 520}}
                                    placement="bottom"
                                    content={
                                        <pre style={{fontSize: 12}} className="block whitespace-pre-wrap">
                    {t("postPage.cards.mediaRequirementsBody")}
                  </pre>
                                    }
                                    title={t("postPage.cards.mediaRequirementsTitle")}
                                >
                                    <div className="flex justify-center gap-1 mt-4 items-center cursor-help">
                                        <InfoCircleFilled style={{color: "blueviolet"}}/>
                                        <span>{t("postPage.misc.imageLabel")}</span>
                                    </div>
                                </Popover>
                            )}
                        </Card>

                        {/* Media Picker / Preview */}
                        {!post?.published && (
                            <Card className="shadow-sm" style={{marginTop: 16}} bodyStyle={{padding: 16}}>
                                <Row justify="space-between" align="middle">
                                    <Typography.Title level={5}
                                                      style={{margin: 0}}>{t("postPage.cards.mediaTitle")}</Typography.Title>
                                    <Space>
                                        {!post?.images?.length && (
                                            <Button icon={<PictureOutlined/>}
                                                    onClick={() => hiddenFileInput.current.click()}>
                                                {t("postPage.buttons.uploadImage")}
                                            </Button>
                                        )}
                                        {(video || post?.video) ? (
                                            <Button danger icon={<VideoCameraOutlined/>} onClick={handleRemoveVideo}>
                                                {t("postPage.buttons.removeVideo")}
                                            </Button>
                                        ) : (
                                            !post?.images?.length &&
                                            contentPlan?.socialNetwork?.includes("Instagram") && (
                                                <Button disabled icon={<VideoCameraOutlined/>}>
                                                    {t("postPage.buttons.addVideoReels")}
                                                </Button>
                                            )
                                        )}
                                        {!post?.images?.length && !images.length && !video && !post?.video && (
                                            <Button icon={<ThunderboltOutlined/>} onClick={onGenerateImage}>
                                                {t("postPage.buttons.generate")}
                                            </Button>
                                        )}
                                    </Space>
                                </Row>

                                <Divider style={{margin: "12px 0"}}/>

                                {/* Drop zone */}
                                <div
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    onClick={post?.images?.length || images?.length ? null : () => hiddenFileInput.current.click()}
                                    className={`${
                                        post?.images?.length || images.length ? "cursor-pointer" : "cursor-pointer border-2 border-dashed"
                                    } ${dragActive ? "border-violet-600 bg-violet-50" : "border-gray-300"} rounded-xl p-4 min-h-24`}
                                >
                                    {images.length ? (
                                        <div className="grid grid-cols-2 gap-3">
                                            {images.map((image, index) => (
                                                <PostPhotoCuter
                                                    key={index}
                                                    index={index}
                                                    image={image}
                                                    handleDeleteImage={handleDeleteImage}
                                                />
                                            ))}
                                        </div>
                                    ) : post?.images?.length ? (
                                        <div className="grid grid-cols-2 gap-3">
                                            {post.images.map((image, index) => (
                                                <PostPhotoEditor
                                                    key={index}
                                                    index={index + 1}
                                                    image={image}
                                                    onImageChange={(img) => {
                                                        const updated = post.images.map((it, i) => (i === index ? img : it));
                                                        handleUpdateImages(updated);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center mt-2 pointer-events-none">
                                            {!video && !post?.video &&
                                                <p className="text-base">{t("postPage.dropzone.noImagesYet")}</p>}
                                            <p className="text-sm text-gray-500">
                                                {dragActive ? t("postPage.dropzone.dropFilesToUpload") : t("postPage.dropzone.dragDropOrClick")}
                                            </p>
                                            <small
                                                className="text-xs text-gray-400 block mt-1">{t("postPage.dropzone.hint")}</small>
                                        </div>
                                    )}

                                    {(video || post?.video) && (
                                        <div className="mt-4">
                                            <video className="mx-auto rounded-lg"
                                                   onLoadedMetadata={handleManageMetaData} ref={videoPlayerRef}
                                                   width="320" height="240" controls>
                                                <source
                                                    onLoadedMetadata={handleManageMetaData}
                                                    src={post?.video ? post.video : URL.createObjectURL(video)}
                                                    type={video?.type || undefined}
                                                />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                    )}

                                    {[...images, video].reduce((s, m) => s + (m?.size || 0), 0) > COMBINED_WARN_LIMIT && (
                                        <p className="text-center text-sm text-red-500 mt-2">
                                            Large total media size — consider compressing.
                                        </p>
                                    )}
                                </div>

                                {/* Secondary actions row */}
                                <div className="mt-3 grid grid-cols-2 gap-8 justify-items-center">
                                    {post?.images?.length ? (
                                        <Button danger
                                                onClick={() => handleRemoveGeneratedImage()}>{t("postPage.buttons.removeImages")}</Button>
                                    ) : (
                                        !!images.length && <Button danger
                                                                   onClick={handleResetImages}>{t("postPage.buttons.removeImages")}</Button>
                                    )}
                                    <PostRegenerateEditor
                                        disabled={postLoading || postGenerating}
                                        onConfirm={(customPrompt) => {
                                            const body = {
                                                originalPostBody: post?.post ?? "",
                                                originalPostHashtags: post?.hashtags ?? "",
                                                originalPostImageRecommendations: post?.media ?? "",
                                                userInstructions: (customPrompt || "").trim(),
                                            };
                                            dispatch(postActions.regeneratePost({
                                                postId: post.id,
                                                contentPlanId: contentPlan.id,
                                                postData: body
                                            }))
                                                .then(() => message.success("Successfully generated"))
                                                .catch(() => message.error("Something went wrong"));
                                        }}
                                    />
                                </div>
                            </Card>
                        )}
                    </Col>

                    {/* RIGHT COLUMN */}
                    <Col xs={24} lg={10}>
                        <LivePreviewCard
                            text={(editMainText ? mainText : (`${post?.post} \n ${post.hashtags}` ?? ""))}
                            serverImages={post?.images}
                            profileName={user?.name || "Company Page"}
                        />
                        <div className="mt-4 flex flex-col gap-2">
                            {/* See post in Social network */}
                            {post?.published &&
                                (post?.published_statuses || []).map((network, idx) =>
                                    network?.url ? (
                                        <Button
                                            key={idx}
                                            type="default"
                                            block
                                            icon={
                                                network?.name?.toLowerCase() === "linkedin" ? <LinkedinOutlined/> :
                                                    network?.name?.toLowerCase() === "facebook" ? <FacebookOutlined/> :
                                                        network?.name?.toLowerCase() === "instagram" ?
                                                            <InstagramOutlined/> : null
                                            }
                                            onClick={() => window.open(network.url, "_blank")?.focus()}
                                        >
                                            {t("posts.seePost")} ({network.name})
                                        </Button>
                                    ) : null
                                )
                            }

                            {/* Create similar post */}
                            <Popconfirm
                                placement="top"
                                title={t("post.selectSocialsMedia")}
                                description={
                                    <div className="py-2 flex items-center justify-center gap-4">
                                        <img
                                            title="LinkedIn"
                                            src={linkedInIcon}
                                            className={`cursor-pointer w-8 ${snForAnotherPost.includes("LinkedIn") ? "" : "opacity-40"}`}
                                            onClick={() => handleToogleSnForAnotherPost("LinkedIn")}
                                        />
                                        <img
                                            title="Facebook"
                                            src={facebookIcon}
                                            className={`cursor-pointer w-8 ${snForAnotherPost.includes("Facebook") ? "" : "opacity-40"}`}
                                            onClick={() => handleToogleSnForAnotherPost("Facebook")}
                                        />
                                        <img
                                            title="Instagram"
                                            src={instagramIcon}
                                            className={`cursor-pointer w-8 ${snForAnotherPost.includes("Instagram") ? "" : "opacity-40"}`}
                                            onClick={() => handleToogleSnForAnotherPost("Instagram")}
                                        />
                                    </div>
                                }
                                onConfirm={handleGenerateAnotherPost}
                                okButtonProps={{disabled: !snForAnotherPost.length}}
                                okText={t("functions.generate")}
                                cancelText={t("functions.close")}
                                icon={<InfoCircleFilled style={{color: "blueviolet"}}/>}
                            >
                                <Button block type="primary">
                                    {t("posts.createAnotherPost")}
                                </Button>
                            </Popconfirm>
                        </div>
                    </Col>
                </Row>

                {/* Hidden inputs (kept) */}
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFilePicker}
                    ref={hiddenFileInput}
                    multiple
                    style={{display: "none"}}
                />
                <input
                    onChange={handleAddVideo}
                    ref={hiddenVideoInput}
                    type="file"
                    accept="video/mp4, video/mov, video/wmv, video/avi, video/flv, video/mpeg, video/mpg, video/quicktime, video/3gpp, video/webm"
                    className="hidden"
                />
            </Layout.Content>

            {/* Bottom Bar */}
            <Layout.Footer
                style={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 9,
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(6px)",
                    width: "100%",
                    borderTop: "1px solid #f0f0f0",
                    padding: "8px 0",
                }}
            >
                <div style={{maxWidth: 1200, margin: "0 auto", padding: "0 16px"}}>
                    <Row align="middle" justify="space-between">
                        <Typography.Text type="secondary" style={{fontSize: 12}}>
                            {t("postPage.footer.draftSaved")}
                        </Typography.Text>
                        <Space>
                            <Button onClick={onOkayButton} disabled={isReadOnly}>{t("postPage.buttons.save")}</Button>
                            <Button type="primary" icon={<SendOutlined/>} onClick={onOkayButton} disabled={isReadOnly}>
                                {t("postPage.buttons.publishFooter")}
                            </Button>
                        </Space>
                    </Row>
                </div>
            </Layout.Footer>
        </Layout>
    );

};
